// @flow

import _ from 'lodash';
import { rem } from 'polished';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { isValidEmail, isZipcodeValid } from 'client/utils/validate';
import { PRE_REG_SUBMIT_FORM } from 'client/constants/amplitudeTracker';
import { renderTextInput } from 'client/components/form/helpers';
import { VIEW_TYPE, FORM_FIELD_NAME } from 'client/components/preRegistration/preRegistration';
import Button from 'client/components/button/button';
import COLOR from 'client/constants/colors';
import FirstAndLastGroup from 'client/components/form/firstAndLastGroup';
import RequiredField from 'client/components/form/requiredField';

import { Tracking } from 'web-core/src/tracking';

type Props = {
  commonTranslations: TranslationType,
  ctaBtn: string,
  header: string,
  isValid: boolean,
  setView: Function,
};

// overwrite default input and error message style
export const RequiredFieldWrapper = styled.div`
  .input-container {
    margin: 0;
    padding: 0;
    max-width: 100%;
    min-height: 5rem;
    label {
      display: none;
    }
    .error-message {
      min-height: 0;
      color: ${COLOR.NAVY_3};
      margin-bottom: 0.5rem;
    }
    .input-wrapper {
      border-color: transparent;
    }
    input {
      height: 3rem;
    }
  }
  .error {
    .input-wrapper {
      border: ${rem(1.5)} solid ${COLOR.RED_2};
    }
  }
`;

const GroupFieldWrapper = styled(RequiredFieldWrapper)`
  .input-group-wrapper {
    .input-container {
      min-height: 0;
      input {
        margin-bottom: 0;
      }
      .error-message {
        margin-bottom: 0;
      }
    }
    .group-error {
      top: 0;
      position: relative;
      margin-bottom: 0.5rem;
      .error-message {
        color: ${COLOR.NAVY_3};
      }
    }
  }
`;

export const ButtonWrapper = styled(Button)`
  background-color: ${COLOR.BLUE_0};
  margin: 0;
  &&.disabled {
    background-color: ${COLOR.BLUE_0};
    opacity: 0.5;
  }
`;

const emailValidator = val =>
  (isValidEmail(val) ? undefined : { path: 'common.error.invalidEmail' });

const zipcodeValidator = val =>
  (isZipcodeValid(val) ? undefined : { path: 'common.error.invalidZip' });

const renderEmailField = _.partial(renderTextInput, _, {
  label: {
    path: 'common.label.email',
  },
  placeholder: 'Email',
  noLabelAnimate: true,
});

const renderZipcodeField = _.partial(renderTextInput, _, {
  label: {
    path: 'common.label.zipCode',
  },
  placeholder: 'Zip Code',
  noLabelAnimate: true,
});

const RegistrationForm = (props: Props) => {
  const { commonTranslations, ctaBtn, header, isValid, setView } = props;
  const { firstName, lastName } = commonTranslations.label;
  const [isDisabled, toggleDisabled] = useState(!isValid);

  useEffect(() => {
    toggleDisabled(!isValid);
  });

  const handleSubmit = () => {
    if (isValid) {
      Tracking.track(PRE_REG_SUBMIT_FORM);
      setView(VIEW_TYPE.TEXT_REMINDER);
    }
  };

  return (
    <React.Fragment>
      <h1 className="small">{header}</h1>
      <GroupFieldWrapper>
        <FirstAndLastGroup placeholder={{ firstName, lastName }} noLabelAnimate />
      </GroupFieldWrapper>
      <RequiredFieldWrapper>
        <RequiredField
          key={FORM_FIELD_NAME.EMAIL}
          name={FORM_FIELD_NAME.EMAIL}
          component={renderEmailField}
          validate={[emailValidator]} />
      </RequiredFieldWrapper>
      <RequiredFieldWrapper>
        <RequiredField
          key={FORM_FIELD_NAME.ZIPCODE}
          name={FORM_FIELD_NAME.ZIPCODE}
          component={renderZipcodeField}
          validate={[zipcodeValidator]} />
      </RequiredFieldWrapper>
      <ButtonWrapper isDisabled={isDisabled} clickHandler={handleSubmit}>
        {ctaBtn}
      </ButtonWrapper>
    </React.Fragment>
  );
};

export default RegistrationForm;
